import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function localDateStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function fmt12(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function fmtDayHeading(dateStr) {
  const today = localDateStr(0);
  if (dateStr === today) return 'Today';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function fmtNextOpening(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtSyncTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function typeShort(type) {
  if (!type) return 'Individual';
  if (/couples/i.test(type)) return 'Couples';
  if (/family/i.test(type)) return 'Family';
  if (/intake|new client/i.test(type)) return 'New Client';
  if (/group/i.test(type)) return 'Group';
  if (/supervision/i.test(type)) return 'Supervision';
  if (/KAP|ketamine/i.test(type)) return 'KAP';
  if (/EMDR/i.test(type)) return 'EMDR';
  return 'Individual';
}

const TYPE_COLOR = {
  Couples: '#8338EC', Family: '#E07A5F', 'New Client': '#2A9D8F',
  Group: '#F4A261', Supervision: '#118AB2', KAP: '#D62246',
  EMDR: '#06A77D', Individual: '#1D2C5F',
};

function StatBox({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 120, background: '#F5F3EF', borderRadius: 10,
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 3,
      borderTop: `3px solid ${accent || '#1D2C5F'}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#878DA1', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1D2C5F', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#878DA1' }}>{sub}</div>}
    </div>
  );
}

// calendarId prop = admin view for a specific therapist
// omit calendarId = therapist sees their own schedule via /my-schedule
export default function ScheduleCard({ calendarId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [dayOffset, setDayOffset] = useState(0);

  const endpoint = calendarId
    ? `${API}/api/acuity/schedule/${calendarId}`
    : `${API}/api/acuity/my-schedule`;

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
      setError(null);
    } catch {
      setError('Could not load schedule data.');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/acuity/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch {
      setError('Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const viewDate = localDateStr(dayOffset);
  const viewAppts = data?.appointmentsByDay?.[viewDate] || [];
  const dayLabel = fmtDayHeading(viewDate);

  // Derive display name from data or user storage
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const displayName = data?.calendarName
    ? data.calendarName.split(',')[0].trim()
    : calendarId ? 'Schedule' : `${user.first_name || ''} ${user.last_name || ''}`.trim();

  if (data?.noCalendar) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.09)', overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ background: '#1D2C5F', padding: '14px 20px' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>My Schedule</div>
        </div>
        <div style={{ padding: '20px', color: '#878DA1', fontSize: 13 }}>
          No Acuity calendar connected to your account yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.09)', overflow: 'hidden', marginBottom: 24 }}>

      {/* Header */}
      <div style={{ background: '#1D2C5F', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{displayName} — Schedule</div>
          {data?.lastSync && (
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 }}>
              Updated {fmtSyncTime(data.lastSync)}
            </div>
          )}
        </div>
        {calendarId && (
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            {syncing ? 'Syncing…' : '↻ Sync'}
          </button>
        )}
      </div>

      {loading && <div style={{ padding: 24, color: '#878DA1', fontSize: 13 }}>Loading schedule…</div>}
      {error && !loading && (
        <div style={{ padding: '16px 20px' }}>
          <p style={{ color: '#c0392b', fontSize: 13, margin: calendarId ? '0 0 10px' : 0 }}>{error}</p>
          {calendarId && (
            <button onClick={handleSync} disabled={syncing} style={{ background: '#1D2C5F', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13 }}>
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          )}
        </div>
      )}

      {!loading && data && (
        <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>

          {/* Left — Day schedule with navigator */}
          <div style={{ flex: '1 1 300px', borderRight: '1px solid #EEF0F6', padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button
                onClick={() => setDayOffset(o => Math.max(0, o - 1))}
                disabled={dayOffset === 0}
                style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, width: 30, height: 30, cursor: dayOffset === 0 ? 'default' : 'pointer', color: dayOffset === 0 ? '#ccc' : '#1D2C5F', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >‹</button>
              <div style={{ textAlign: 'center', flex: 1, padding: '0 8px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1D2C5F' }}>{dayLabel}</div>
                {dayOffset === 0 && <div style={{ fontSize: 11, color: '#878DA1', marginTop: 1 }}>Tap › to peek ahead</div>}
              </div>
              <button
                onClick={() => setDayOffset(o => Math.min(5, o + 1))}
                disabled={dayOffset === 5}
                style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, width: 30, height: 30, cursor: dayOffset === 5 ? 'default' : 'pointer', color: dayOffset === 5 ? '#ccc' : '#1D2C5F', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >›</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 14 }}>
              {[0,1,2,3,4,5].map(i => (
                <button key={i} onClick={() => setDayOffset(i)}
                  style={{ width: i === dayOffset ? 18 : 7, height: 7, borderRadius: 4, background: i === dayOffset ? '#1D2C5F' : '#D1D5DB', border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.2s' }}
                />
              ))}
            </div>

            {viewAppts.length === 0 ? (
              <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>
                {!data.lastSync ? 'No data yet — click Sync.' : 'No appointments.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
                {viewAppts.map((appt, i) => {
                  const label = typeShort(appt.appointment_type);
                  const color = TYPE_COLOR[label] || '#1D2C5F';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', minWidth: 74 }}>{fmt12(appt.appt_datetime)}</div>
                      <div style={{ fontSize: 13, color: '#444', flex: 1 }}>{appt.client_first_name} {appt.client_last_name?.charAt(0)}.</div>
                      <span style={{ fontSize: 11, fontWeight: 600, color, background: color + '18', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #F0EDE8', fontSize: 12, color: '#878DA1' }}>
              {viewAppts.length} appointment{viewAppts.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Right — Stats */}
          <div style={{ flex: '1 1 260px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#878DA1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Next 7 Days
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <StatBox label="Appointments" value={data.appointmentsNext7} sub="scheduled" accent="#1D2C5F" />
              <StatBox label="Openings" value={data.openings7} sub="available slots" accent="#2A9D8F" />
            </div>
            <div style={{ marginTop: 6 }}>
              <StatBox
                label="Next Opening"
                value={fmtNextOpening(data.nextOpeningDate)}
                sub={data.nextOpeningTime ? fmt12(data.nextOpeningTime) : (data.nextOpeningDate ? '' : 'none found in range')}
                accent="#C67A52"
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
